// Library methods
import { useMemo, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MUI Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

// Components
import ExamCardShared from "../../../../components/UI/ExamCardShared";
import {
  DiagonalArrowsIcon,
  LinkIcon,
  VRIcon,
  EditIcon,
  ViewFileIcon,
} from "../../../../assets/icons";

// Utilities
import {
  getCompletedExamsTooltip,
  getExamCardText,
  getTooltipText,
} from "../../../../utils/examReportsHelper";
import { useLiveUpdateStatusContext } from "../../../../contexts/LiveUpdateStatusContext";
import { getTruncatedText } from "../../../../utils/textHelper";

const TodayExamCard = forwardRef(
  (
    {
      row,
      patientId,
      handleEditClick,
      handleLinkDeviceClick,
      handleUnlinkDeviceClick,
      associatedExamDevices,
      linkLoading,
      unlinkLoading,
    },
    ref
  ) => {
    // internationalization
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
      exam,
      examStatus,
      device,
      hasDevice,
      testCompletionIcon,
      isEditDisabled,
      checkReportDisabled,
      checkLinkDeviceDisabled,
      checkTooltipDeviceMessage,
      checkLiveUpdateDisabled,
    } = ExamCardShared(row, associatedExamDevices);

    const { actions } = useLiveUpdateStatusContext();
    const { handleViewLiveUpdateClick } = actions;

    // truncated name
    const patientName = useMemo(
      () =>
        getTruncatedText(
          `${exam.patient?.firstName} ${exam.patient?.lastName}`
        ),
      [exam.patient]
    );

    const isMultiExamPackage = exam?.sectionTypeOrder?.length > 1;

    // exam related helper values
    const examTranslation = useMemo(() => getExamCardText(exam, t), [exam, t]);
    const examTooltipTranslation = useMemo(
      () => getTooltipText(exam, t),
      [exam, t]
    );
    const completedExamSections = useMemo(
      () => getCompletedExamsTooltip(exam, t),
      [exam, t]
    );

    // buttons are ordered in terms of importance / usage
    const examMenuButtons = useMemo(
      () => [
        {
          icon: <ViewFileIcon />,
          tooltipText: t("word_view_results"),
          text: t("button_view_report"),
          onClick: () => {},
          display: !checkReportDisabled,
          href: `/patients/${patientId}/exam/${exam?.id}`,
        },
        {
          icon: <EditIcon />,
          text: t("word_edit"),
          tooltipText: t("exams_edit_modal_title"),
          onClick: () => handleEditClick(exam),
          display: !isEditDisabled,
          href: undefined,
        },
      ],
      [t, exam, patientId, isEditDisabled, checkReportDisabled, handleEditClick]
    );

    // because of the device section being split, we needed to add a grid offset to each of the items inside the card
    return (
      <Box ref={ref}>
        <Grid
          container
          py={0}
          sx={{ display: "flex", alignItems: "center", flexWrap: "no-wrap" }}
        >
          <Grid item xs={1.8}>
            {!hasDevice && (
              <Tooltip
                title={checkTooltipDeviceMessage()}
                PopperProps={{
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                }}
              >
                <Card
                  disabled={checkLinkDeviceDisabled() || linkLoading}
                  elevation={0}
                  key={exam.id}
                  sx={{
                    mb: 2,
                    mr: 2,
                    borderRadius: 2,
                    backgroundColor: "#E2772E",
                    "&:hover": {
                      backgroundColor: "#CE631A",
                    },
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                    color: "white",
                    height: { xs: 120, lg: 52 },
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    flexWrap: "wrap",
                    // when the card is disabled, add change cursor and add a grey backgroundColor
                    ...(checkLinkDeviceDisabled() && {
                      cursor: "auto",
                      backgroundColor: "#5B6D7A70",
                      "&:hover": {
                        backgroundColor: "#5B6D7A70",
                      },
                    }),
                  }}
                  // only enable click method when the card is not disabled
                  {...(!checkLinkDeviceDisabled() &&
                    !linkLoading && {
                      onClick: () => handleLinkDeviceClick(exam),
                    })}
                >
                  <VRIcon />
                  <LinkIcon />
                </Card>
              </Tooltip>
            )}
            {hasDevice && (
              <Tooltip title={checkTooltipDeviceMessage()}>
                <Card
                  elevation={0}
                  key={exam.id}
                  sx={{
                    mb: 2,
                    mr: 2,
                    borderRadius: 2,
                    backgroundColor: "#202338",
                    color: "white",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                    height: { xs: 120, lg: 52 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    px: 1,
                  }}
                  disabled={unlinkLoading}
                  onClick={() => handleUnlinkDeviceClick(device)}
                >
                  <VRIcon />
                  <Typography
                    sx={{
                      display: "block",
                      width: "100%",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      fontSize: {
                        xs: "12px",
                        md: "14px",
                      },
                    }}
                  >
                    {device?.alias}
                  </Typography>
                </Card>
              </Tooltip>
            )}
          </Grid>
          <Grid item xs={10.2}>
            <Card
              elevation={0}
              key={exam.id}
              sx={{
                mb: "16px",
                py: "8px",
                borderRadius: "8px",
                backgroundColor: "#202338",
                color: "#FFF",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                height: { xs: 120, lg: 52 },
                display: "flex",
                jutifyContent: "center",
                alignItems: "center",
              }}
            >
              <Grid
                container
                py={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-around",
                }}
              >
                <Grid
                  item
                  sm={10}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    alignItems: { xs: "start", lg: "center" },
                    justifyContent: { lg: "space-between" },
                  }}
                >
                  <Grid item xs={12} sm={3} sx={{ minWidth: "100px" }}>
                    <Typography
                      sx={{
                        display: "block",
                        fontSize: {
                          xs: "12px",
                          md: "14px",
                        },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {patientName}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={3}
                    sx={{
                      ml: { xs: 0 },
                      width: "100%",
                    }}
                  >
                    <Tooltip
                      disableHoverListener={
                        !isMultiExamPackage ||
                        !examStatus?.status.includes(" / ")
                      }
                      title={
                        <div style={{ whiteSpace: "pre-line" }}>
                          {completedExamSections}
                        </div>
                      }
                      placement="bottom-start"
                    >
                      <span>
                        <Typography
                          sx={{
                            display: "block",
                            fontSize: {
                              xs: "12px",
                              md: "14px",
                            },
                            whiteSpace: "nowrap",
                          }}
                        >
                          {examStatus?.status}
                        </Typography>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        display: "block",
                        fontSize: {
                          xs: "12px",
                          md: "14px",
                        },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {testCompletionIcon}
                    </Box>
                  </Grid>
                  <Tooltip
                    title={
                      <div style={{ whiteSpace: "pre-line" }}>
                        {examTooltipTranslation}
                      </div>
                    }
                    placement="bottom-start"
                  >
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        ml: { xs: 0 },
                        width: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          display: "block",
                          fontSize: {
                            xs: "12px",
                            md: "14px",
                          },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {examTranslation}
                      </Typography>
                    </Grid>
                  </Tooltip>
                </Grid>
                <Grid item sm={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", lg: "end" },
                    }}
                  >
                    <Tooltip title={t("tooltip_live_update_panel")}>
                      <span>
                        <IconButton
                          size="medium"
                          sx={{
                            height: "40px",
                            color: "inherit",
                            "&.Mui-disabled": {
                              color: "inherit",
                              opacity: "0.5",
                            },
                            display:
                              examStatus?.status === t("word_completed") ||
                              examStatus?.status === t("word_terminated")
                                ? "none"
                                : "block",
                            "@keyframes fade-in-out": {
                              "0%": {
                                opacity: "0.3",
                              },
                              "50%": {
                                opacity: "1",
                              },
                              "100%": {
                                opacity: "0.3",
                              },
                            },
                            animation: checkLiveUpdateDisabled()
                              ? "none"
                              : "fade-in-out 2s infinite",
                          }}
                          disabled={checkLiveUpdateDisabled()}
                          onClick={() => {
                            handleViewLiveUpdateClick(device?.id);
                          }}
                        >
                          <DiagonalArrowsIcon />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {examMenuButtons.map((examButton) => (
                      <Tooltip
                        key={examButton.tooltipText}
                        title={examButton.tooltipText}
                      >
                        <span>
                          <a
                            href={examButton.href}
                            style={{ textDecoration: "none", color: "inherit" }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (examButton.href) navigate(examButton.href);
                              else examButton.onClick();
                            }}
                          >
                            <IconButton
                              sx={{
                                height: "40px",
                                color: "inherit",
                                "&.Mui-disabled": {
                                  color: "inherit",
                                  opacity: "0.5",
                                },
                                display: !examButton.display ? "none" : "block",
                              }}
                              disabled={!examButton.display}
                            >
                              {examButton.icon}
                            </IconButton>
                          </a>
                        </span>
                      </Tooltip>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }
);

export default TodayExamCard;
